// src/components/LongShortBoard.jsx
// 보유종목 한눈에 — 롱(좌) · 숏(우) 좌우 분할. 비중(그로스 대비)·당일 등락률.
import React, { useEffect, useState } from "react";
import { portfolioExposure, SIDE_BOOK_KRW } from "../lib/exposure";
import { sectorOf } from "../lib/sectors";
import { fetchMomoStocks } from "../hooks/useSheets";
import AddPairModal from "./AddPairModal";
import { pairLegs, pairId } from "../lib/pairs";
import { findByName } from "../lib/krStocks";

const LONG = "#0a7c59", SHORT = "#c0392b";
const pct = (n, d = 1) => (n == null || isNaN(n)) ? "—" : `${n.toFixed(d)}%`;
const sgn = (n, d = 1) => (n == null || isNaN(n)) ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(d)}`;
// 시트에서 빈문자열("")로 오면 == null 이 false라 .toFixed 크래시 → 숫자 아니면 null
const numOrNull = (v) => (v == null || v === "" || isNaN(v)) ? null : Number(v);
// 시트가 코드의 앞자리 0을 떼서 숫자로 저장(005930→5930) → 앞자리 0 제거한 형태로 매칭.
// 티커 자리에 종목명이 든 행(단선매도 등)은 숫자가 하나도 없어 전부 ""로 뭉개져 서로 충돌 →
// useMarketData.resolveFetch와 동일하게 종목명으로 코드를 해석해서 키를 만든다.
const bareCode = (t, name) => {
  const digits = String(t == null ? "" : t).replace(/\D/g, "").replace(/^0+/, "");
  if (digits) return digits;
  const hit = findByName(name || t);
  return hit ? String(hit.c).replace(/^0+/, "") : "";
};

export default function LongShortBoard({ holdings, marketData, exchangeRates, nav, isMobile, onPairChart, pairs = [], onAddPair, onDeletePair, onUpdatePair, onReorderPairs }) {
  const [momoStocks, setMomoStocks] = useState({});   // code(앞0제거) → {beta,stm,mtm}
  const [showAddPair, setShowAddPair] = useState(false);
  const [editingPair, setEditingPair] = useState(null);
  const movePair = (from, to) => {
    if (from == null || to == null || from === to || !onReorderPairs) return;
    const next = [...pairs];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onReorderPairs(next);
  };
  const rbtn = (dis) => ({ border: "none", background: "none", cursor: dis ? "default" : "pointer", color: dis ? "#eee" : "#aaa", fontSize: 9, lineHeight: 1, padding: 0 });
  const [extraQuotes, setExtraQuotes] = useState({});   // 페어에 있으나 보유엔 없는 종목 시세(code→{price,changePct})
  useEffect(() => {
    let alive = true;
    fetchMomoStocks().then((j) => {
      if (!alive) return;
      const norm = {};
      Object.keys(j.stocks || {}).forEach((k) => { norm[bareCode(k)] = j.stocks[k]; });
      setMomoStocks(norm);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // 페어 레그 중 보유에 없는 종목의 라이브 시세 조회 (현재가/등락률 표시용)
  useEffect(() => {
    const held = new Set(holdings.map((h) => bareCode(h.ticker, h.name)));
    const syms = [];
    (pairs || []).forEach((p) => {
      const { longs: pl, shorts: ps } = pairLegs(p);
      [...pl, ...ps].forEach((l) => {
        if (l.ticker && !held.has(bareCode(l.ticker, l.name))) syms.push(`${l.ticker}:${l.market || "KR"}`);
      });
    });
    const uniq = [...new Set(syms)];
    if (!uniq.length) { setExtraQuotes({}); return; }
    let alive = true;
    fetch(`/api/quote-stream?symbols=${encodeURIComponent(uniq.join(","))}`)
      .then((r) => r.text())
      .then((text) => {
        if (!alive) return;
        const map = {};
        text.trim().split("\n").forEach((ln) => {
          try { const o = JSON.parse(ln); if (o && o.data) map[bareCode(o.ticker)] = o.data; } catch {}
        });
        setExtraQuotes(map);
      }).catch(() => {});
    return () => { alive = false; };
  }, [pairs, holdings]);

  const { rows, grossKRW, longKRW, shortKRW } = portfolioExposure(holdings, marketData, exchangeRates);
  const base = nav > 0 ? nav : (grossKRW || 1);  // NAV 대비 % (미입력 시 그로스 대체)
  const navPct = (krw) => (krw / base) * 100;

  const enrich = (r) => {
    const md = marketData[r.ticker];
    const wGross = grossKRW ? r.exposureKRW / grossKRW : 0;    // 그로스 대비 비중
    const m = momoStocks[bareCode(r.ticker, r.name)] || {};
    return {
      ticker: r.ticker,
      name: md?.name || r.name || r.ticker,
      market: r.market || "KR",
      short: r.short,
      sector: sectorOf(r.ticker, r.market),
      weight: (r.exposureKRW / SIDE_BOOK_KRW) * 100,  // 고정 북사이즈(80억) 대비
      changePct: md?.changePct,
      price: md?.price ?? null,
      // 당일 수익기여(%): (숏 -1) × 그로스비중 × 등락률
      contribution: md?.changePct != null ? (r.short ? -1 : 1) * wGross * md.changePct : null,
      beta: numOrNull(m.beta), stm: numOrNull(m.stm), mtm: numOrNull(m.mtm),
      hasPrice: r.hasPrice,
    };
  };
  const longs = rows.filter((r) => !r.short).map(enrich).sort((a, b) => b.weight - a.weight);
  const shorts = rows.filter((r) => r.short).map(enrich).sort((a, b) => b.weight - a.weight);
  const byTicker = {};
  const byAnySide = {};
  // 같은 종목을 롱·숏 동시 보유(단선 헤지)하면 코드가 겹치므로 사이드까지 키에 넣는다.
  [...longs, ...shorts].forEach((it) => {
    const c = bareCode(it.ticker, it.name);
    byTicker[(it.short ? "S|" : "L|") + c] = it;
    byAnySide[c] = it;
  });
  // 페어 레그 데이터: 보유종목이면 전체지표, 아니면 라이브 시세만(비중·기여 없음)
  const legData = (ticker, name, short) => {
    const bc = bareCode(ticker, name);
    const hit = byTicker[(short ? "S|" : "L|") + bc] || byAnySide[bc];
    if (hit) return hit;
    const q = extraQuotes[bc];
    if (q) return { price: q.price ?? null, changePct: q.changePct ?? null, weight: null, contribution: null, notHeld: true };
    return null;
  };
  // 수정: 기존 페어 삭제 후 새 페어 추가
  const handlePairSubmit = (pair) => {
    if (editingPair && onUpdatePair) onUpdatePair(editingPair, pair);
    else if (onAddPair) onAddPair(pair);
    setEditingPair(null);
  };

  const Side = ({ title, items, color, totalKRW }) => (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "12px 16px", borderBottom: `2px solid ${color}`, background: color === LONG ? "#f2faf6" : "#fdf3f2" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>{title}</span>
        <span style={{ fontSize: 11, color: "#888" }}>
          {items.length}종목 · NAV대비 {pct(navPct(totalKRW))}
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 330 }}>
          {/* 컬럼 헤더 */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", fontSize: 9, color: "#bbb", fontWeight: 700, borderBottom: "1px solid #f0f0f0" }}>
            <span style={{ width: 26, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 56 }}>종목</span>
            <span style={{ width: 38, textAlign: "right", flexShrink: 0 }}>비중</span>
            <span style={{ width: 42, textAlign: "right", flexShrink: 0 }}>일%</span>
            <span style={{ width: 44, textAlign: "right", flexShrink: 0 }}>기여</span>
            <span style={{ width: 34, textAlign: "right", flexShrink: 0 }}>β</span>
            <span style={{ width: 36, textAlign: "right", flexShrink: 0 }}>STM</span>
            <span style={{ width: 36, textAlign: "right", flexShrink: 0 }}>MTM</span>
          </div>
          {items.length === 0 ? (
            <div style={{ textAlign: "center", color: "#bbb", padding: 20, fontSize: 12 }}>없음</div>
          ) : items.map((it, i) => (
            <div key={it.ticker} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderTop: i === 0 ? "none" : "1px solid #f6f6f6" }}>
              <button onClick={() => onPairChart && onPairChart(it)} title={`${it.short ? "롱" : "숏"} 종목 붙여 페어 차트 보기`}
                style={{ width: 26, height: 22, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #cdd7e6", background: "#eef3fb", color: "#1f3a93", borderRadius: 6, padding: 0, fontSize: 13, fontWeight: 800, cursor: "pointer", lineHeight: 1 }}>⇄</button>
              <span style={{ flex: 1, minWidth: 56, fontSize: 12, fontWeight: 600, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={it.sector}>{it.name}</span>
              <span style={{ width: 38, textAlign: "right", flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: "#333" }}>{pct(it.weight)}</span>
              <span style={{ width: 42, textAlign: "right", flexShrink: 0, fontSize: 11.5, fontWeight: 600, color: it.changePct == null ? "#bbb" : it.changePct >= 0 ? LONG : SHORT }}>
                {it.changePct == null ? "—" : `${it.changePct >= 0 ? "+" : ""}${it.changePct.toFixed(1)}`}
              </span>
              <span style={{ width: 44, textAlign: "right", flexShrink: 0, fontSize: 11, fontWeight: 600, color: (it.contribution ?? 0) >= 0 ? LONG : SHORT }}>{sgn(it.contribution, 2)}</span>
              <span style={{ width: 34, textAlign: "right", flexShrink: 0, fontSize: 11, color: "#666" }}>{it.beta == null ? "—" : it.beta.toFixed(2)}</span>
              <span style={{ width: 36, textAlign: "right", flexShrink: 0, fontSize: 11, color: "#666" }}>{it.stm == null ? "—" : sgn(it.stm, 1)}</span>
              <span style={{ width: 36, textAlign: "right", flexShrink: 0, fontSize: 11, color: "#666" }}>{it.mtm == null ? "—" : sgn(it.mtm, 1)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // 페어 패널 — 롱/숏 페어별로 각 레그의 비중·현재가·기여 비교 (pairs=차트북 추가분)
  const PairPanel = () => (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "12px 16px", borderBottom: "2px solid #1f3a93", background: "#f2f5fb" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: "#1f3a93" }}>🔗 페어</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "#888" }}>{pairs.length}쌍</span>
          {onAddPair && (
            <button onClick={() => setShowAddPair(true)} title="페어 추가"
              style={{ border: "none", background: "#1f3a93", color: "#fff", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+ 페어</button>
          )}
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 290 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", fontSize: 9, color: "#bbb", fontWeight: 700, borderBottom: "1px solid #f0f0f0" }}>
            <span style={{ width: 12, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 40 }}>종목</span>
            <span style={{ width: 38, textAlign: "right", flexShrink: 0 }}>비중</span>
            <span style={{ width: 54, textAlign: "right", flexShrink: 0 }}>현재가</span>
            <span style={{ width: 40, textAlign: "right", flexShrink: 0 }}>일%</span>
            <span style={{ width: 42, textAlign: "right", flexShrink: 0 }}>기여</span>
          </div>
          {pairs.length === 0 ? (
            <div style={{ textAlign: "center", color: "#bbb", padding: 20, fontSize: 12 }}>차트북에서 페어를 추가하세요</div>
          ) : pairs.map((p, i) => {
            const { longs: pl, shorts: ps } = pairLegs(p);
            const legItems = [
              ...pl.map((l) => ({ tag: "L", name: l.name || l.ticker, it: legData(l.ticker, l.name, false), color: LONG })),
              ...ps.map((l) => ({ tag: "S", name: l.name || l.ticker, it: legData(l.ticker, l.name, true), color: SHORT })),
            ];
            const net = legItems.reduce((s, x) => s + (x.it?.contribution ?? 0), 0);
            const leg = (tag, name, it, tagColor, key) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 12px", fontSize: 11 }}>
                <span style={{ width: 12, flexShrink: 0, color: tagColor, fontWeight: 800 }}>{tag}</span>
                <span style={{ flex: 1, minWidth: 40, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#1a1a2e", fontWeight: 600 }}>{name}</span>
                <span style={{ width: 38, textAlign: "right", flexShrink: 0, color: "#666" }}>{it ? pct(it.weight) : "—"}</span>
                <span style={{ width: 54, textAlign: "right", flexShrink: 0, color: "#333" }}>{it && it.price != null ? Math.round(it.price).toLocaleString() : "—"}</span>
                <span style={{ width: 40, textAlign: "right", flexShrink: 0, fontWeight: 600, color: it && it.changePct != null ? (it.changePct >= 0 ? LONG : SHORT) : "#bbb" }}>{it && it.changePct != null ? `${it.changePct >= 0 ? "+" : ""}${it.changePct.toFixed(1)}` : "—"}</span>
                <span style={{ width: 42, textAlign: "right", flexShrink: 0, fontWeight: 600, color: (it?.contribution ?? 0) >= 0 ? LONG : SHORT }}>{it ? sgn(it.contribution, 2) : "—"}</span>
              </div>
            );
            return (
              <div key={pairId(p)} style={{ borderTop: i === 0 ? "none" : "1px solid #eee", padding: "4px 0", display: "flex", alignItems: "stretch" }}>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1, padding: "0 4px 0 6px" }}>
                  <button onClick={() => movePair(i, 0)} disabled={i === 0} title="맨 위로" style={rbtn(i === 0)}>⏫</button>
                  <button onClick={() => movePair(i, i - 1)} disabled={i === 0} title="위로" style={rbtn(i === 0)}>▲</button>
                  <button onClick={() => movePair(i, i + 1)} disabled={i === pairs.length - 1} title="아래로" style={rbtn(i === pairs.length - 1)}>▼</button>
                  <button onClick={() => movePair(i, pairs.length - 1)} disabled={i === pairs.length - 1} title="맨 아래로" style={rbtn(i === pairs.length - 1)}>⏬</button>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                {legItems.map((x, j) => leg(x.tag, x.name, x.it, x.color, j))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 8px 2px 12px" }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, color: net >= 0 ? LONG : SHORT }}>페어기여 {sgn(net, 2)}</span>
                  <span style={{ display: "flex", gap: 2 }}>
                    {onAddPair && (
                      <button onClick={() => { setEditingPair(p); setShowAddPair(true); }} title="페어 수정"
                        style={{ border: "none", background: "none", color: "#aaa", cursor: "pointer", fontSize: 11, lineHeight: 1, padding: 2 }}>✎</button>
                    )}
                    {onDeletePair && (
                      <button onClick={() => onDeletePair(p)} title="페어 삭제"
                        style={{ border: "none", background: "none", color: "#ccc", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: 2 }}>✕</button>
                    )}
                  </span>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 800, color: "#1a1a2e" }}>
        보유종목 한눈에 — <span style={{ color: LONG }}>롱 {pct(navPct(longKRW))}</span> · <span style={{ color: SHORT }}>숏 {pct(navPct(shortKRW))}</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: "#aaa" }}> (NAV 대비)</span>
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(330px, 1fr))", gap: 12, alignItems: "start" }}>
        <Side title="🟢 LONG" items={longs} color={LONG} totalKRW={longKRW} />
        <Side title="🔴 SHORT" items={shorts} color={SHORT} totalKRW={shortKRW} />
        <PairPanel />
      </div>
      {showAddPair && onAddPair && (
        <AddPairModal holdings={holdings} initial={editingPair} onAdd={handlePairSubmit} onClose={() => { setShowAddPair(false); setEditingPair(null); }} />
      )}
    </div>
  );
}
